import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import { SelectOption } from '@missionlabs/react'
import { NumbersPhoneNumber, WhatsAppNumber } from '@missionlabs/types'
import { useMemo } from 'react'
import { useGetNumbersForUserQuery, useGetWhatsAppNumbersQuery } from 'shared/store'

import { useFormatToNumberE164 } from './useFormatToNumberE164'
import { Features, useHasClientFeature } from './useHasClientFeature'
import { useHasFeature } from './useHasFeature'

type PhoneNumberWithTeam = NumbersPhoneNumber & { teamName?: string }

// Stable references so consumers' memos/effects don't re-run while there is no data
const NO_WHATSAPP_NUMBERS: WhatsAppNumber[] = []
const NO_NUMBERS: {
    myWhatsAppNumbers: PhoneNumberWithTeam[]
    myWhatsAppNumberOptions: SelectOption[]
} = { myWhatsAppNumbers: [], myWhatsAppNumberOptions: [] }

/**
 * Gets a list of WhatsApp numbers for the user
 */
export const useGetMyWhatsAppNumbers = () => {
    const isWhatsAppEnabled = useHasFeature(Features.whatsapp)
    const isTeamClientEnabled = useHasClientFeature(Features.teams)
    const { formatToLocalNumber } = useFormatToNumberE164()

    const user = useSelector(selectAuthenticatedUser)
    const { data: userNumberQuery } = useGetNumbersForUserQuery(user?.userID ?? '')
    const { data: whatsappNumbers = NO_WHATSAPP_NUMBERS } = useGetWhatsAppNumbersQuery(undefined, {
        skip: !isWhatsAppEnabled
    })

    const { myWhatsAppNumbers, myWhatsAppNumberOptions } = useMemo(() => {
        if (!userNumberQuery) return NO_NUMBERS

        const { phoneNumbers = [], teams = [] } = userNumberQuery

        const teamNumbers = teams.flatMap(team => {
            if (!isTeamClientEnabled || !team.phoneNumbers?.length) return []
            return team.phoneNumbers.map(number => {
                return { ...number, teamName: team.name }
            })
        })

        const allNumbers: PhoneNumberWithTeam[] = [...phoneNumbers, ...teamNumbers]

        const myWhatsAppNumbers = allNumbers.filter(number =>
            whatsappNumbers.some(whatsappNumber => whatsappNumber.numberID === number.ID)
        )

        const myWhatsAppNumberOptions: SelectOption[] = myWhatsAppNumbers.map(number => {
            const formattedNumber = formatToLocalNumber(number.numberE164)
            const label = number.teamName
                ? `${formattedNumber} - ${number.teamName}`
                : formattedNumber
            return { label, value: number.ID }
        })

        return { myWhatsAppNumbers, myWhatsAppNumberOptions }
    }, [userNumberQuery, whatsappNumbers, isTeamClientEnabled, formatToLocalNumber])

    return { myWhatsAppNumbers, myWhatsAppNumberOptions }
}
